/**
 * Construit la file ordonnée des `CgDocType` attendus dans la checklist
 * Documents, à partir du dossier standard (`DEFAULT_DOCUMENT_QUEUE`) enrichi de
 * blocs conditionnels.
 *
 * Extrait du `useMemo` de `FormCGIAv2` pour être PUR et testable. Cas couverts :
 *  - demandes GOD complémentaires (mode édition) ;
 *  - pack hébergement (attestation déposée) ;
 *  - Récépissé DA (Parcours A) : détecté OCR (vendeur pro ET titulaire CG
 *    particulier), pièce déjà déposée, OU déclaré par l'agence à l'étape 1
 *    (`isProfessionalResale`) ;
 *  - acquéreur personne morale (Kbis ajouté, permis retiré) ;
 *  - succession déclarée par l'agence à l'étape 1 (`isSuccession`) : ajout des
 *    blocs « Acte de décès » + « Justificatif de succession ».
 */

import { DEFAULT_DOCUMENT_QUEUE } from '../config/documentFlowConfig';
import { CgDocType } from '../ocr/ocrResponseTypes';

export interface BuildEffectiveDocQueueParams {
  isEditMode: boolean;
  /** docTypes demandés par GOD (cg_document_requests) en mode édition. */
  openRequestDocTypes: readonly CgDocType[];
  /** Une attestation d'hébergement a été déposée. */
  hasAttestationHebergement: boolean;
  /** Un récépissé de déclaration d'achat a déjà été déposé. */
  hasRecepisseDa: boolean;
  /**
   * L'OCR a détecté un vendeur personne morale sur la cession (le flag
   * `vendeur_est_personne_morale`, et LUI SEUL).
   *
   * ⚠️ À ne pas confondre avec `vendeurEstPersonneMorale` plus bas, volontairement
   * distinct : celui-ci pilote le Parcours A (récépissé de déclaration d'achat) et
   * reste cantonné au signal le plus sûr. L'élargir aux autres signaux (SIRET
   * vendeur, SIREN du C.1) ferait exiger un récépissé sur des dossiers qui n'en
   * relèvent pas.
   */
  vendeurEstPro: boolean;
  /**
   * Le titulaire de la carte grise est un PARTICULIER (aucun SIREN au C.1). C'est
   * une condition du Parcours A : un intermédiaire professionnel rachète à un
   * particulier puis revend. Si le titulaire CG est lui-même une société (ex.
   * une entreprise qui revend son propre véhicule = vente directe), ce n'est PAS
   * un Parcours A et le récépissé n'est pas requis — même garde que la règle
   * cross-check `parcoursARequis`.
   */
  cgTitulaireParticulier: boolean;
  /**
   * Une analyse OCR est encore en cours (au moins une pièce en upload/analyse).
   * Tant que c'est vrai, on ne matérialise PAS le récépissé depuis la détection
   * OCR (vendeur pro) : les valeurs sont partielles et une cession lue avant la
   * carte grise ferait clignoter un faux « Parcours A ». On attend le verdict
   * final. Les déclencheurs déclaratifs (récépissé déposé, rachat pro déclaré à
   * l'étape 1) restent actifs pendant l'analyse.
   */
  analysisInProgress: boolean;
  /** L'acquéreur est une personne morale (Kbis / SIRET / raison sociale). */
  acheteurEstPersonneMorale: boolean;
  /**
   * Le VENDEUR est une personne morale (SIRET / raison sociale de la cession, ou
   * SIREN du C.1 de la carte grise).
   *
   * Matérialise un slot `kbis_vendeur` DISTINCT de celui de l'acquéreur : un
   * dossier peut compter deux sociétés (13 des 26 dossiers avec Kbis depuis juin
   * 2026), et un seul emplacement faisait que le second extrait écrasait le
   * premier. Ce Kbis n'est pas exigé par le SIV : il sert uniquement à
   * pré-remplir raison sociale et SIRET du vendeur dans « Soumettre le CTVO »,
   * et n'entre donc NI dans `RecapAgencyScreen.REQUIRED`, NI dans les demandes
   * de pièces adressées à l'agence.
   *
   * Plus large que `vendeurEstPro` (qui ne lit que le flag de la cession) : ici
   * un SIRET vendeur ou un SIREN au C.1 suffisent. Les deux paramètres coexistent
   * à dessein, ils ne déclenchent pas la même chose.
   */
  vendeurEstPersonneMorale?: boolean;
  /** L'agence a déclaré une succession à l'étape 1. */
  isSuccession: boolean;
  /** L'agence a déclaré un rachat par un professionnel (Parcours A) à l'étape 1. */
  isProfessionalResale: boolean;
  /**
   * Un co-titulaire ACHETEUR est actif (checkbox agence ou détection 13750) :
   * sa pièce d'identité (recto + verso) devient une ligne à part entière de la
   * checklist — dissociation demandée par François (retours 2026-07-29).
   */
  hasCotitulaireAcheteur: boolean;
  /** La ligne VERSO de la pièce d'identité du titulaire doit être listée (cf. `shouldListVersoRow`). */
  listPiTitulaireVerso: boolean;
  /** Idem pour le verso du permis du titulaire. */
  listPermisTitulaireVerso: boolean;
  /** Idem pour le verso de la pièce du co-titulaire. */
  listPiCotitulaireVerso: boolean;
}

/** Vue minimale d'un document déposé, suffisante pour les gardes verso. */
interface UploadedDocLike {
  ocrResponse?: { fields?: Record<string, { value?: unknown }> };
}

/**
 * `true` si la pièce d'identité déposée est un passeport (champ OCR
 * `type_document`) — un passeport n'a pas de verso, sa ligne VERSO ne doit
 * donc pas apparaître. Pur → testable directement.
 */
export const isPasseport = (doc?: UploadedDocLike): boolean =>
  doc?.ocrResponse?.fields?.['type_document']?.value === 'passeport';

/**
 * Décide si la ligne VERSO d'une pièce d'identité doit figurer dans la
 * checklist (dissociation RECTO/VERSO, retours François 2026-07-29) :
 *  - verso déposé (slot routé occupé) → sa ligne existe, toujours ;
 *  - rien reçu → on liste l'attente des DEUX faces d'emblée ;
 *  - recto reçu : CNI/titre de séjour dont l'OCR confirme `est_recto_seul`
 *    → le verso manque réellement, la ligne reste. Passeport (pas de verso)
 *    ou scan contenant déjà les deux faces → pas de ligne (faux manquant).
 *  - permis : pas de flag `est_recto_seul` fiable côté OCR → on ne réclame
 *    jamais un verso probablement contenu dans le même fichier (PDF 2 pages).
 * Pur → testable directement.
 */
export const shouldListVersoRow = (
  rectoDoc: UploadedDocLike | undefined,
  versoDoc: UploadedDocLike | undefined,
  kind: 'cni' | 'permis',
): boolean => {
  if (versoDoc) return true;
  if (!rectoDoc) return true;
  if (kind === 'permis') return false;
  if (isPasseport(rectoDoc)) return false;
  return rectoDoc.ocrResponse?.fields?.['est_recto_seul']?.value === true;
};

/**
 * Compagnon VERSO de chaque pièce d'identité listée recto/verso (le passeport
 * et les scans deux-faces sont gérés par les gardes `list*Verso`).
 */
const VERSO_COMPANIONS: Partial<Record<CgDocType, CgDocType>> = {
  piece_identite: 'piece_identite_verso',
  permis_conduire: 'permis_conduire_verso',
  piece_identite_cotitulaire: 'piece_identite_cotitulaire_verso',
};

export const buildEffectiveDocQueue = ({
  isEditMode,
  openRequestDocTypes,
  hasAttestationHebergement,
  hasRecepisseDa,
  vendeurEstPro,
  cgTitulaireParticulier,
  analysisInProgress,
  acheteurEstPersonneMorale,
  vendeurEstPersonneMorale,
  isSuccession,
  isProfessionalResale,
  hasCotitulaireAcheteur,
  listPiTitulaireVerso,
  listPermisTitulaireVerso,
  listPiCotitulaireVerso,
}: BuildEffectiveDocQueueParams): CgDocType[] => {
  const base: CgDocType[] = [...DEFAULT_DOCUMENT_QUEUE];

  // Demandes GOD complémentaires en mode édition (ex: kbis).
  if (isEditMode && openRequestDocTypes.length > 0) {
    const standard = new Set<CgDocType>(DEFAULT_DOCUMENT_QUEUE);
    openRequestDocTypes.forEach((docType) => {
      if (!standard.has(docType) && !base.includes(docType)) {
        base.push(docType);
      }
    });
  }

  // Pack hébergement — matérialisé UNIQUEMENT quand une attestation
  // d'hébergement est réellement déposée (cf. note historique FormCGIAv2).
  if (hasAttestationHebergement) {
    const jdcIndex = base.indexOf('justif_domicile');
    let cursor = jdcIndex >= 0 ? jdcIndex + 1 : base.length;
    if (!base.includes('attestation_hebergement')) {
      base.splice(cursor, 0, 'attestation_hebergement');
      cursor += 1;
    }
    if (!base.includes('piece_identite_hebergeur')) {
      base.splice(cursor, 0, 'piece_identite_hebergeur');
    }
  }

  // Slot Récépissé de déclaration d'achat (Parcours A) — apparaît quand :
  //  - l'OCR détecte un intermédiaire pro : vendeur personne morale ET titulaire
  //    CG particulier (mêmes conditions que la règle `parcoursARequis`) ; un
  //    vendeur pro qui revend SON propre véhicule (titulaire CG = société) reste
  //    une vente directe classique → pas de récépissé. Ce déclencheur OCR n'est
  //    pris en compte qu'une fois l'analyse TERMINÉE (rideau anti-flash : une
  //    cession lue avant la carte grise ferait clignoter un faux Parcours A) ;
  //  - un récépissé a déjà été déposé ;
  //  - OU l'agence a déclaré le rachat pro à l'étape 1.
  const ocrDetecteParcoursA = vendeurEstPro && cgTitulaireParticulier && !analysisInProgress;
  if ((ocrDetecteParcoursA || hasRecepisseDa || isProfessionalResale) && !base.includes('recepisse_da')) {
    base.push('recepisse_da');
  }

  // Acquéreur personne morale : on matérialise le slot Kbis et on retire le
  // permis de conduire (une société n'en a pas).
  if (acheteurEstPersonneMorale) {
    if (!base.includes('kbis_acheteur')) {
      const jdcIdx = base.indexOf('justif_domicile');
      if (jdcIdx >= 0) base.splice(jdcIdx, 0, 'kbis_acheteur');
      else base.push('kbis_acheteur');
    }
    const permisIdx = base.indexOf('permis_conduire');
    if (permisIdx >= 0) base.splice(permisIdx, 1);
  }

  // Vendeur personne morale : son propre slot Kbis, juste après la cession qui
  // le désigne. Il ne retire AUCUNE pièce de l'acquéreur — c'est tout l'objet de
  // la séparation des deux slots.
  if (vendeurEstPersonneMorale && !base.includes('kbis_vendeur')) {
    const cessionIdx = base.indexOf('cerfa_cession');
    if (cessionIdx >= 0) base.splice(cessionIdx + 1, 0, 'kbis_vendeur');
    else base.push('kbis_vendeur');
  }

  // Pack succession déclaré par l'agence à l'étape 1 : acte de décès du
  // titulaire + justificatif de succession (acte de notoriété ou attestation
  // des héritiers). Cf. référentiel CTVO §4-bis.
  if (isSuccession) {
    if (!base.includes('acte_deces')) base.push('acte_deces');
    if (!base.includes('justif_succession')) base.push('justif_succession');
  }

  // Co-titulaire ACHETEUR actif : sa pièce d'identité entre dans la checklist
  // comme ligne à part entière (au lieu du seul sous-bloc dédié), juste après
  // la pièce du titulaire. Son permis n'est pas requis ANTS → pas de ligne.
  if (hasCotitulaireAcheteur && !base.includes('piece_identite_cotitulaire')) {
    const piIdx = base.indexOf('piece_identite');
    if (piIdx >= 0) base.splice(piIdx + 1, 0, 'piece_identite_cotitulaire');
    else base.push('piece_identite_cotitulaire');
  }

  // Dissociation RECTO/VERSO (retours François 2026-07-29) : chaque pièce
  // d'identité listée est suivie de sa ligne VERSO quand la garde
  // `shouldListVersoRow` la juge pertinente (passeport et scans deux-faces
  // exclus). Les items d'ingestion étant réétiquetés avec le slot routé
  // (`retagIngestionItems`), un verso déposé allume sa propre ligne.
  const versoListed: Partial<Record<CgDocType, boolean>> = {
    piece_identite_verso: listPiTitulaireVerso,
    permis_conduire_verso: listPermisTitulaireVerso,
    piece_identite_cotitulaire_verso: listPiCotitulaireVerso,
  };
  return base.flatMap((docType) => {
    const verso = VERSO_COMPANIONS[docType];
    const withVerso = verso && versoListed[verso] && !base.includes(verso);
    return withVerso ? [docType, verso] : [docType];
  });
};
